import { Suspense, lazy, useState } from 'react'
import { deleteWithUndo } from '@/lib/actions'
import { QUICK_CHAT } from '@/lib/lead'
import { createPortal } from 'react-dom'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useKeyboardOpen } from '@/hooks/useVisualViewport'
import { RemindPicker } from '@/components/RemindPicker'
import { PageThread } from '@/components/PageThread'
import { PrimePanel } from '@/components/PrimePanel'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { api, mutate } from '@/runtime'
import { useAllPages, useAllProjects, useAllTasks, useLook, useProjectOf, useRecord } from '@/db/hooks'
import type { PageSpec, PageStatus } from '@/kinds/page/schema'
import type { PageResource } from '@/kinds/page/apply'
import { PagesSection, sortPages } from '@/kinds/page/components'
import { ListsSection } from '@/kinds/list/components'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { useShareWithItem } from '@/components/ShareWith'
import { ConvertSheet } from '@/components/ConvertSheet'
import { coverCss } from '@/kinds/page/covers'
import { isLightTheme } from '@/lib/colour'
import { useUi } from '@/state/ui'
import { EmojiPicker } from '@/components/EmojiPicker'
import { Icon } from '@/lib/icons'
import type { EditorApi } from '@/kinds/page/editor'
import { useAssistantEnabled } from '@/components/Suggestions'
import { Button, Sheet, SideSheet, cx } from '@/components/ui'
import { Gone } from './NotFound'
import {
  CheckSquare,
  ClipboardCopy,
  FolderInput,
  History,
  Info,
  MoreHorizontal,
  Pin,
  Repeat2,
  Sparkles,
} from 'lucide-react'

// BlockNote + Mantine are ~1 MB; only the page route pays for them
const PageEditor = lazy(() => import('@/kinds/page/editor').then((m) => ({ default: m.PageEditor })))
import { PageHeader } from './page/PageHeader'
import { ConflictBanner } from './page/ConflictBanner'
import { blocksToText } from '@/kinds/page/text'
import { LinkTaskSheet } from './page/LinkTaskSheet'
import { CoverSheet } from './page/CoverSheet'
import { MoveSheet } from './page/MoveSheet'
import { Panel, savedText } from './page/Panel'
import { LabelsPanel } from './page/LabelsPanel'
import { LinkedTasks } from './page/LinkedTasks'
import { HistorySheet } from './page/HistorySheet'
import { useAccess } from '@/state/access'
import { usePanel } from '@/state/panel'
import { PANEL_QUERY } from '@/lib/panel'

export const PageView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const page = useRecord<PageSpec, PageStatus>(id) as PageResource | null | undefined
  const pages = useAllPages() ?? []
  const projects = useAllProjects() ?? []
  const tasks = useAllTasks() ?? []
  const parentOf = useProjectOf()
  const look = useLook()
  const light = isLightTheme(look.theme, look.palette)
  const flash = useUi((s) => s.showFlash)
  const focusMode = useUi((s) => s.focusMode)
  const toggleFocus = useUi((s) => s.toggleFocus)
  const rail = useUi((s) => s.rightRailEl)
  const desktop = useMediaQuery('(min-width: 768px)')
  const keyboardOpen = useKeyboardOpen()
  const [picking, setPicking] = useState(false)
  const [coverOpen, setCoverOpen] = useState(false)
  const [moving, setMoving] = useState(false)
  const [editorApi, setEditorApi] = useState<EditorApi | null>(null)
  const assistant = useAssistantEnabled()
  const wide = useMediaQuery(PANEL_QUERY)
  const [linkOpen, setLinkOpen] = useState(false)
  // bumped by the AI entry points: the page's thread puts the cursor in its box
  const [askSignal, setAskSignal] = useState(0)
  const [conflict, setConflict] = useState<unknown[] | null>(null)
  // phone: the side panel lives in three sheets behind a toolbar above the tab bar
  const [tool, setTool] = useState<'details' | 'agent' | 'actions' | null>(null)
  const [converting, setConverting] = useState(false)
  const [history, setHistory] = useState(false)
  const member = useAccess((s) => s.member)
  // the page's AI is one thread (docs/prime-panel.md): beside the page on a wide
  // screen, in the details column on a narrower desktop, the Agent tab on a phone
  const ai = !!assistant && !member
  const shareWithItem = useShareWithItem(
    page ? { kind: 'Page', id: page.id, title: page.name, ws: page.labels.ws } : null,
  )
  // with the chat docked beside the page, the details fold behind a button: three columns
  // and the sidebar left the editor about 450px
  const panelOpen = usePanel((s) => s.open)
  const folded = wide && panelOpen && ai
  const [detailsOpen, setDetailsOpen] = useState(false)

  if (page === undefined) return null
  if (!page || page.deleted_at) return <Gone kind="page" deleted={!!page?.deleted_at} />

  // breadcrumb: walk belongs_to upwards through pages to a project or task
  const crumbs: { label: React.ReactNode; to: string; params: { id: string } }[] = []
  let cur = parentOf.get(page.id)
  for (let i = 0; cur && i < 16; i++) {
    const pg = pages.find((p) => p.id === cur)
    if (pg) {
      crumbs.unshift({
        label: (
          <>
            <Icon value={pg.spec.icon} className="mr-1" />
            {pg.name}
          </>
        ),
        to: '/pages/$id',
        params: { id: pg.id },
      })
      cur = parentOf.get(pg.id)
      continue
    }
    const proj = projects.find((p) => p.id === cur)
    if (proj)
      crumbs.unshift({
        label: (
          <>
            <Icon value={proj.spec.icon} fallback="▣" className="mr-1" />
            {proj.spec.title}
          </>
        ),
        to: '/projects/$id',
        params: { id: proj.id },
      })
    const task = tasks.find((t) => t.id === cur)
    if (task) crumbs.unshift({ label: `○ ${task.spec.title}`, to: '/tasks/$id', params: { id: task.id } })
    break
  }
  const back = crumbs.at(-1)

  // pages this one may move under: same workspace, not itself, not a descendant
  const descendants = new Set<string>()
  const stack = [page.id]
  while (stack.length) {
    const p = stack.pop()!
    for (const c of pages)
      if (parentOf.get(c.id) === p && !descendants.has(c.id)) {
        descendants.add(c.id)
        stack.push(c.id)
      }
  }
  const sameWs = (r: { labels: Record<string, string> }) => r.labels.ws === page.labels.ws
  const moveTargets = {
    pages: sortPages(
      pages.filter((p) => sameWs(p) && p.id !== page.id && !descendants.has(p.id) && !p.deleted_at),
    ),
    projects: projects.filter((p) => sameWs(p) && p.status.state !== 'done' && p.status.state !== 'dropped'),
  }
  const parent = parentOf.get(page.id) ?? null
  const move = async (to: string | null) => {
    await mutate.setProject(page.id, to)
    setMoving(false)
  }
  const remove = async () => {
    await deleteWithUndo(page.id, page.name || page.spec.title)
    if (back) void navigate({ to: back.to, params: back.params })
    else void navigate({ to: '/pages' })
  }
  // the header's AI, /ai and the toolbar's Ask AI all open the page's thread
  const askAi = () => {
    if (wide) {
      usePanel.getState().setOpen(true)
      usePanel.getState().setTab('thread')
    } else if (!desktop) setTool('agent')
    setAskSignal((n) => n + 1)
  }
  const copyMarkdown = async () => {
    if (!editorApi) return
    try {
      await navigator.clipboard.writeText(await editorApi.markdown())
      flash('Copied as markdown')
    } catch {
      flash('Could not copy')
    }
  }
  const cover = coverCss(page.spec.cover)

  return (
    <div className="md:flex md:gap-8 md:items-start" data-testid="page-view">
      {/* editor column */}
      <div className={cx('min-w-0 flex-1', !desktop && !focusMode && 'pb-16')}>
        {!desktop && !focusMode && (
          <div className="flex items-center justify-between px-1 pb-2">
            {back ? (
              <Link
                to={back.to}
                params={back.params}
                className="text-sm text-muted hover:text-fg inline-flex items-center gap-1.5"
              >
                ← {back.label}
              </Link>
            ) : (
              <Link to="/pages" className="text-sm text-muted hover:text-fg inline-flex items-center gap-1.5">
                ← All pages
              </Link>
            )}
            <span className="text-xs text-faint">Saved {savedText(page.status.last_touched)}</span>
          </div>
        )}
        <PageHeader
          page={page}
          cover={cover}
          // a phone's way in is the toolbar's Agent tab: one entry, not a second "AI" above the title
          assistant={ai && desktop}
          focusMode={focusMode}
          onToggleFocus={toggleFocus}
          onCover={() => setCoverOpen(true)}
          onIcon={() => setPicking(true)}
          onAi={askAi}
          onDetails={folded ? () => setDetailsOpen(true) : undefined}
        />
        <div className="px-1 md:px-6">
          {conflict && editorApi && (
            <ConflictBanner
              theirs={conflict}
              assistant={!!assistant}
              onKeepMine={() => editorApi.keepMine()}
              onTakeTheirs={() => editorApi.takeTheirs(conflict)}
              onMerge={async () => {
                const mine = await editorApi.markdown()
                const theirs = blocksToText(conflict)
                const res = await api.assistantAsk({
                  mode: 'write',
                  workspace: page.labels.ws,
                  page_id: page.id,
                  page_title: page.spec.title,
                  page_text: mine,
                  prompt:
                    'Two devices edited this page at once. Merge the two versions into one page that keeps every change from both, without duplicating anything. Return the whole merged page.',
                  selection: `VERSION FROM THE OTHER DEVICE:\n${theirs}`,
                })
                if (!res.text?.trim()) throw new Error('Nothing came back')
                await editorApi.takeMerged(res.text)
              }}
            />
          )}
          <Suspense
            fallback={<div className="min-h-[40vh] text-sm text-faint px-2 py-4">Loading editor…</div>}
          >
            <PageEditor
              key={page.id}
              page={page}
              light={light}
              onReady={setEditorApi}
              onAskAi={ai ? askAi : undefined}
              onLinkTask={() => setLinkOpen(true)}
              onConflict={setConflict}
            />
          </Suspense>
        </div>
      </div>

      {/* metadata panel: one aside on desktop (portalled into the right rail), three sheets on the phone */}
      {(() => {
        const backBlock = back ? (
          <Link
            to={back.to}
            params={back.params}
            className="text-sm text-muted hover:text-fg inline-flex items-center gap-1.5"
          >
            ← {back.label}
          </Link>
        ) : (
          <Link to="/pages" className="text-sm text-muted hover:text-fg inline-flex items-center gap-1.5">
            ← All pages
          </Link>
        )
        const saved = (
          <p className="text-xs text-faint" data-testid="page-saved">
            Saved {savedText(page.status.last_touched)}
          </p>
        )
        const detailsBlock = (
          <>
            {/* where the page belongs, said first: the project (or task) at the top of its crumbs */}
            <Panel
              title={crumbs[0]?.to === '/tasks/$id' ? 'Task' : 'Project'}
              right={
                <Button variant="chip" onClick={() => setMoving(true)}>
                  Move…
                </Button>
              }
            >
              {crumbs[0] && crumbs[0].to !== '/pages/$id' ? (
                <Link
                  to={crumbs[0].to}
                  params={crumbs[0].params}
                  className="text-sm hover:underline"
                  data-testid="page-details-project"
                >
                  {crumbs[0].label}
                </Link>
              ) : (
                <p className="text-sm text-faint">Not in a project.</p>
              )}
            </Panel>
            <LabelsPanel page={page} />
            <LinkedTasks page={page} />
            <PagesSection
              parentId={page.id}
              ws={page.labels.ws}
              title="Sub-pages"
              className="pt-5 border-t border-line"
            />
            <ListsSection parentId={page.id} ws={page.labels.ws} className="pt-5 border-t border-line" />
          </>
        )
        const agentBlock = ai && <PageThread page={page} editorApi={editorApi} focusSignal={askSignal} />
        const actions: ActionItem[] = [
          {
            id: 'pin',
            label: page.spec.pinned ? 'Pinned' : 'Pin',
            icon: Pin,
            pin: true,
            pressed: !!page.spec.pinned,
            onSelect: () => void mutate.patch(page.id, { spec: { pinned: !page.spec.pinned } }),
            testid: 'page-pin',
          },
          {
            id: 'move',
            label: 'Move…',
            icon: FolderInput,
            onSelect: () => setMoving(true),
            testid: 'page-move',
          },
          shareWithItem,
          {
            id: 'convert',
            label: 'Convert to…',
            icon: Repeat2,
            onSelect: () => setConverting(true),
            testid: 'page-convert',
          },
          {
            id: 'remind',
            label: 'Remind me',
            render: ({ as }) => (
              <RemindPicker
                as={as}
                value={page.spec.remind_at}
                fired={page.status.reminder_fired_for === page.spec.remind_at}
                onChange={(v) => void mutate.patch(page.id, { spec: { remind_at: v } })}
              />
            ),
          },
          {
            id: 'checklists',
            label: 'Checklists → tasks',
            icon: CheckSquare,
            disabled: !editorApi,
            onSelect: () => void editorApi?.convertChecklists('all'),
            testid: 'page-checklists-to-tasks',
            title: 'Every checklist item on the page becomes a task block (a real task, linked here)',
          },
          {
            id: 'history',
            label: 'History',
            icon: History,
            hidden: member,
            onSelect: () => {
              setTool(null)
              setHistory(true)
            },
            testid: 'page-history',
          },
          {
            id: 'copy',
            label: 'Copy as markdown',
            icon: ClipboardCopy,
            disabled: !editorApi,
            onSelect: () => void copyMarkdown(),
            testid: 'page-copy-md',
          },
          {
            id: 'delete',
            label: 'Delete',
            tone: 'danger',
            onSelect: () => void remove(),
            confirm: {
              label: descendants.size
                ? `Delete with ${descendants.size} sub-page${descendants.size === 1 ? '' : 's'}`
                : 'Delete page',
              testid: 'page-delete-confirm',
            },
            testid: 'page-delete',
          },
        ]
        const actionsBlock = (
          <ActionBar
            items={actions}
            label="Page actions"
            testid="page-actions"
            layout={desktop ? 'auto' : 'rows'}
          />
        )
        if (desktop) {
          const panel = (
            <aside
              hidden={focusMode}
              className={cx('w-full shrink-0 flex flex-col gap-5', rail && !focusMode ? '' : 'md:w-72')}
              aria-label="Page details"
            >
              <div>
                {backBlock}
                <div className="mt-2">{saved}</div>
              </div>
              {detailsBlock}
              {/* at PANEL_QUERY the thread is in the panel beside the page instead */}
              {!wide && agentBlock && (
                <section className="pt-5 border-t border-line">
                  <h3 className="text-sm font-semibold mb-2 inline-flex items-center gap-1.5">
                    <Sparkles size={14} className="text-accent" aria-hidden /> {QUICK_CHAT}
                  </h3>
                  {agentBlock}
                </section>
              )}
              <Panel title="Actions">{actionsBlock}</Panel>
            </aside>
          )
          if (folded)
            return (
              <SideSheet open={detailsOpen} onClose={() => setDetailsOpen(false)} title="Details">
                {panel}
              </SideSheet>
            )
          return rail && !focusMode ? createPortal(panel, rail) : panel
        }
        // phone: sheet bodies drop the first hairline so they start flush under the title
        const body = 'flex flex-col gap-5 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0'
        const toolClass = (on: boolean) =>
          cx(
            'flex-1 inline-flex items-center justify-center gap-1.5 rounded-full min-h-10 text-sm transition-colors',
            on ? 'bg-accent-soft text-fg' : 'text-muted active:bg-raised',
          )
        return (
          <>
            {!focusMode && !keyboardOpen && (
              <div
                className="fixed inset-x-3 z-20 bottom-[calc(var(--above-tabbar)+0.75rem)] flex gap-1 rounded-full bg-panel/95 backdrop-blur-md border border-line p-1 shadow-[var(--shadow-pop)]"
                role="toolbar"
                aria-label="Page tools"
                data-testid="page-tools"
              >
                <button
                  type="button"
                  className={toolClass(tool === 'details')}
                  onClick={() => setTool('details')}
                  data-testid="page-tools-details"
                >
                  <Info size={15} aria-hidden /> Details
                </button>
                {ai && (
                  <button
                    type="button"
                    className={toolClass(tool === 'agent')}
                    onClick={askAi}
                    data-testid="page-tools-agent"
                  >
                    <Sparkles size={15} aria-hidden /> {QUICK_CHAT}
                  </button>
                )}
                <button
                  type="button"
                  className={toolClass(tool === 'actions')}
                  onClick={() => setTool('actions')}
                  data-testid="page-tools-actions"
                >
                  <MoreHorizontal size={15} aria-hidden /> Actions
                </button>
              </div>
            )}
            <Sheet open={tool === 'details'} onClose={() => setTool(null)} title="Details">
              <div className={body}>{detailsBlock}</div>
            </Sheet>
            <Sheet open={tool === 'agent'} onClose={() => setTool(null)} title={QUICK_CHAT}>
              {/* the page it is about, under the one name the chat has everywhere */}
              <p className="text-xs text-faint -mt-2 mb-2 truncate" data-testid="page-chat-about">
                About {page.name || page.spec.title || 'this page'}
              </p>
              {agentBlock}
            </Sheet>
            <Sheet open={tool === 'actions'} onClose={() => setTool(null)} title="Actions">
              <div className="flex flex-col gap-4">
                {actionsBlock}
                <div className="flex items-center justify-between pt-3 border-t border-line">
                  {backBlock}
                  {saved}
                </div>
              </div>
            </Sheet>
          </>
        )
      })()}

      {/* the page's thread docked beside it, on a wide screen only (drawn by the panel itself) */}
      <PrimePanel page={page} editorApi={editorApi} focusSignal={askSignal} />

      {linkOpen && (
        <LinkTaskSheet page={page} tasks={tasks} editorApi={editorApi} onClose={() => setLinkOpen(false)} />
      )}
      <EmojiPicker
        open={picking}
        value={page.spec.icon}
        onClose={() => setPicking(false)}
        onPick={(e) => {
          void mutate.patch(page.id, { spec: { icon: e ?? null } })
          setPicking(false)
        }}
      />
      {coverOpen && <CoverSheet page={page} onClose={() => setCoverOpen(false)} />}
      {history && <HistorySheet page={page} onClose={() => setHistory(false)} />}
      {converting && <ConvertSheet source={page} tasks={[]} onClose={() => setConverting(false)} />}
      {moving && (
        <MoveSheet
          parent={parent}
          projects={moveTargets.projects}
          pages={moveTargets.pages}
          onMove={move}
          onClose={() => setMoving(false)}
        />
      )}
    </div>
  )
}
